import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useCallback, useEffect, useMemo, useState } from "react";

export type WeightNumberInputProps = {
  id?: string;
  value: number; // Value in milligrams
  onChange?: (value: number) => void;
  unit?: WeightUnit; // Fixed unit if provided, does not allow other selection
  defaultUnit?: WeightUnit; // default displayed unit, does allow selection of other units
  className?: string;
  disabled?: boolean;
  locale?: string; // Optional locale, defaults to 'de-DE' for ',' as decimal separator
};

export function WeightNumberInput({
  id,
  value,
  onChange,
  unit,
  defaultUnit,
  className = "",
  disabled = false,
  locale = "de-DE",
}: WeightNumberInputProps) {
  const [selectedUnit, setSelectedUnit] = useState<WeightUnit>(
    defaultUnit || WeightUnit.Grams,
  );
  const [inputValue, setInputValue] = useState<string>("0");

  const activeUnit = unit || selectedUnit;

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [locale],
  );

  const formatLocaleNumber = useCallback(
    (number: number) => formatter.format(Number.isNaN(number) ? 0 : number),
    [formatter],
  );

  const parseLocaleNumber = useCallback(
    (input: string) => {
      const separator = formatter.format(1.1).charAt(1);
      const normalized = input.replace(separator, ".");
      return parseFloat(normalized);
    },
    [formatter],
  );

  const convertFromMilligrams = useCallback((mg: number, u: WeightUnit) => {
    switch (u) {
      case WeightUnit.Grams:
        return mg / 1000;
      case WeightUnit.Kilograms:
        return mg / 1e6;
      default:
        return mg;
    }
  }, []);

  const convertToMilligrams = useCallback((v: number, u: WeightUnit) => {
    switch (u) {
      case WeightUnit.Grams:
        return v * 1000;
      case WeightUnit.Kilograms:
        return v * 1e6;
      default:
        return v;
    }
  }, []);

  useEffect(() => {
    setInputValue(formatLocaleNumber(convertFromMilligrams(value, activeUnit)));
  }, [value, activeUnit, formatLocaleNumber, convertFromMilligrams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleBlurOrEnter = () => {
    const parsedValue = parseLocaleNumber(inputValue);
    if (!Number.isNaN(parsedValue)) {
      const milligrams = convertToMilligrams(parsedValue, activeUnit);
      const roundedValue = convertFromMilligrams(milligrams, activeUnit);
      setInputValue(formatLocaleNumber(roundedValue));
      if (onChange) {
        onChange(milligrams);
      }
    } else {
      setInputValue(
        formatLocaleNumber(convertFromMilligrams(value, activeUnit)),
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleBlurOrEnter();
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        className="h-8 w-24 text-end"
        value={inputValue}
        disabled={disabled}
        onChange={handleInputChange}
        onBlur={handleBlurOrEnter}
        onKeyDown={handleKeyDown}
      />
      {unit ? (
        <span className="flex h-8 items-center px-1">
          {WeightUnitUtil.toString(unit)}
        </span>
      ) : (
        <Select
          value={selectedUnit}
          defaultValue={defaultUnit || WeightUnit.Grams}
          onValueChange={(val) => setSelectedUnit(val as WeightUnit)}
        >
          <SelectTrigger className="h-8 w-fit">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={WeightUnit.Milligrams}>
              {WeightUnitUtil.toString(WeightUnit.Milligrams)}
            </SelectItem>
            <SelectItem value={WeightUnit.Grams}>
              {WeightUnitUtil.toString(WeightUnit.Grams)}
            </SelectItem>
            <SelectItem value={WeightUnit.Kilograms}>
              {WeightUnitUtil.toString(WeightUnit.Kilograms)}
            </SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export enum WeightUnit {
  Milligrams = "milligrams",
  Grams = "grams",
  Kilograms = "kilograms",
}

export class WeightUnitUtil {
  private static readonly descriptions: {
    [key in WeightUnit]: string;
  } = {
    [WeightUnit.Milligrams]: "Milligramm",
    [WeightUnit.Grams]: "Gramm",
    [WeightUnit.Kilograms]: "Kilogramm",
  };

  private static readonly abbreviations: {
    [key in WeightUnit]: string;
  } = {
    [WeightUnit.Milligrams]: "mg",
    [WeightUnit.Grams]: "g",
    [WeightUnit.Kilograms]: "kg",
  };

  static toString(type: WeightUnit): string {
    return this.abbreviations[type];
  }

  static toLongString(type: WeightUnit): string {
    return this.descriptions[type] || "Unknown";
  }
}
